import { useEffect, useMemo, useState } from "react";
import { useCatalog } from "@/providers/CatalogProvider";
import type { ScaleEntry } from "@/types/scale";

interface SubdomainNode {
  key: string;
  domainCode: string;
  domainName: string;
  subdomainCode: string;
  subdomainLabel: string;
  subdomainName: string;
  entries: ScaleEntry[];
}

interface DomainNode {
  domainCode: string;
  domainName: string;
  subdomains: SubdomainNode[];
}

export function ScaleLibraryPage(): JSX.Element {
  const { scaleLibrary } = useCatalog();

  const domainTree = useMemo<DomainNode[]>(() => {
    return scaleLibrary.domains.map((domain) => ({
      domainCode: domain.domainCode,
      domainName: domain.domainName,
      subdomains: domain.subdomains.map((subdomain) => {
        const key = buildSubdomainKey(domain.domainCode, subdomain.subdomainCode);
        const entries = scaleLibrary.entries
          .filter(
            (entry) =>
              entry.domainCode === domain.domainCode &&
              entry.subdomainCode === subdomain.subdomainCode,
          )
          .sort((a, b) => a.code.localeCompare(b.code, "zh-CN"));
        return {
          key,
          domainCode: domain.domainCode,
          domainName: domain.domainName,
          subdomainCode: subdomain.subdomainCode,
          subdomainLabel: subdomain.subdomainLabel,
          subdomainName: subdomain.subdomainName,
          entries,
        };
      }),
    }));
  }, [scaleLibrary.domains, scaleLibrary.entries]);

  const [activeSubdomainKey, setActiveSubdomainKey] = useState<string>("");

  useEffect(() => {
    if (!activeSubdomainKey && domainTree.length > 0) {
      const firstSubdomain = domainTree[0]?.subdomains[0];
      if (firstSubdomain) {
        setActiveSubdomainKey(firstSubdomain.key);
      }
    }
  }, [domainTree, activeSubdomainKey]);

  const subdomainEntriesMap = useMemo(() => {
    const map = new Map<string, ScaleEntry[]>();
    domainTree.forEach((domain) =>
      domain.subdomains.forEach((subdomain) => {
        map.set(subdomain.key, subdomain.entries);
      }),
    );
    return map;
  }, [domainTree]);

  const activeEntries = subdomainEntriesMap.get(activeSubdomainKey) ?? [];

  const activeMetadata = useMemo(() => {
    for (const domain of domainTree) {
      const subdomain = domain.subdomains.find((item) => item.key === activeSubdomainKey);
      if (subdomain) {
        return { domain, subdomain };
      }
    }
    return undefined;
  }, [domainTree, activeSubdomainKey]);

  const entriesPerDomain = useMemo(() => {
    const map = new Map<string, number>();
    domainTree.forEach((domain) => {
      const total = domain.subdomains.reduce((sum, subdomain) => sum + subdomain.entries.length, 0);
      map.set(domain.domainCode, total);
    });
    return map;
  }, [domainTree]);

  return (
    <div className="p-4 md:p-6">
      <h1 className="sr-only">查看量表</h1>
      <div className="grid gap-4 lg:grid-cols-[18rem_minmax(0,1fr)] lg:items-start">
        <aside
          aria-label="量表领域与子领域"
          className="w-full rounded-xl border border-slate-200 bg-white shadow-sm lg:sticky lg:top-6 lg:max-h-[calc(100dvh-7rem)] lg:overflow-y-auto"
        >
          <nav aria-label="量表目录" className="divide-y divide-slate-200">
            {domainTree.map((domain) => {
              const total = entriesPerDomain.get(domain.domainCode) ?? 0;
              return (
                <div key={domain.domainCode} className="p-4">
                  <h2 className="text-sm font-semibold text-slate-700">
                    {domain.domainName}（{total} 条）
                  </h2>
                  <ul className="mt-3 space-y-2 text-sm">
                    {domain.subdomains.map((subdomain) => {
                      const isActive = activeSubdomainKey === subdomain.key;
                      return (
                        <li key={subdomain.key}>
                          <button
                            type="button"
                            onClick={() => setActiveSubdomainKey(subdomain.key)}
                            className={`flex min-h-11 w-full flex-col justify-center rounded-md border px-3 py-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 ${
                              isActive
                                ? "border-brand bg-brand/10 text-brand"
                                : "border-transparent hover:border-slate-200 hover:bg-slate-50"
                            }`}
                          >
                            <span className="font-medium">{subdomain.subdomainLabel}</span>
                            <span className="text-xs text-slate-500">
                              共 {subdomain.entries.length} 条目
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </nav>
        </aside>

        <section className="min-w-0 space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          {activeMetadata ? (
            <div>
              <h2 className="text-lg font-semibold text-slate-800">
                {activeMetadata.domain.domainName} · {activeMetadata.subdomain.subdomainLabel}
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                共 {activeEntries.length} 条量表条目，可用于教学或活动设计参考。
              </p>
            </div>
          ) : (
            <p className="text-sm text-slate-500">请选择左侧子领域查看条目。</p>
          )}

          <div className="space-y-4">
            {activeEntries.map((entry) => (
              <ScaleEntryCard key={entry.code} entry={entry} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function buildSubdomainKey(domainCode: string, subdomainCode: string): string {
  return `${domainCode}:${subdomainCode}`;
}

function ScaleEntryCard({ entry }: { entry: ScaleEntry }): JSX.Element {
  const goalTitle = entry.shortTermGoal?.title ?? entry.longTermGoal.title;
  const goalLevelLabel = entry.level === "long_term" ? "长期目标" : "短期目标";

  return (
    <article className="rounded-lg border border-slate-200 bg-slate-50 p-4 shadow-sm">
      <header className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {entry.domainName} · {entry.subdomainLabel}
          </p>
          <h3 className="text-base font-semibold text-slate-800">
            {entry.code} · {goalTitle}
          </h3>
        </div>
        <span className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600">
          {goalLevelLabel}
        </span>
      </header>
      <p className="mt-3 text-sm text-slate-700">{entry.standard}</p>
      {entry.examples ? (
        <p className="mt-2 text-xs text-slate-500">示例：{entry.examples}</p>
      ) : null}
      {entry.activities.length > 0 ? (
        <p className="mt-2 text-xs text-slate-500">
          推荐活动：<span className="text-slate-600">{entry.activities.join("、")}</span>
        </p>
      ) : null}
    </article>
  );
}
